import { useCallback, useEffect, useRef } from 'react'
import { ShoppingCart, UtensilsCrossed, BarChart3, Maximize2, Minimize2 } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { useAppStore } from '@/lib/store/useAppStore'
import { useUIStore } from '@/lib/store/useUIStore'
import { POSTablesView } from '@/components/pos/POSTablesView'
import { POSTerminal } from '@/components/pos/POSTerminal'
import { POSMenuView } from '@/components/pos/POSMenuView'
import { POSReportsView } from '@/components/pos/POSReportsView'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { usePOSMenuSync } from '@/lib/queries/usePOS'

const TABS = [
  { key: 'tables' as const, label: 'Tables', icon: ShoppingCart },
  { key: 'menu' as const, label: 'Menu', icon: UtensilsCrossed },
  { key: 'reports' as const, label: 'Reports', icon: BarChart3 },
]

const POS_MODE_LABEL = {
  hybrid: 'Hybrid POS',
  integrated: 'Tables bound',
  standalone: 'Standalone',
}

export default function POSPage() {
  usePOSMenuSync()
  const fullscreenRootRef = useRef<HTMLDivElement>(null)
  const featurePOS = useAppStore((s) => s.featurePOS)
  const posMode = useAppStore((s) => s.posMode)
  const posTab = useUIStore((s) => s.posTab)
  const setPosTab = useUIStore((s) => s.setPosTab)
  const posFullscreen = useUIStore((s) => s.posFullscreen)
  const setPosFullscreen = useUIStore((s) => s.setPosFullscreen)
  const isStandalone = posMode === 'standalone'
  const tabs = TABS.map((tab) =>
    tab.key === 'tables'
      ? { ...tab, label: isStandalone ? 'Terminal' : 'Tables' }
      : tab,
  )

  const enterFullscreen = useCallback(async () => {
    setPosFullscreen(true)
    try {
      await fullscreenRootRef.current?.requestFullscreen?.()
    } catch {
      // CSS fullscreen remains active when native fullscreen is unavailable.
    }
  }, [setPosFullscreen])

  const exitFullscreen = useCallback(async () => {
    setPosFullscreen(false)
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
    } catch {
      // The CSS fullscreen state is already cleared.
    }
  }, [setPosFullscreen])

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) setPosFullscreen(false)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [setPosFullscreen])

  useEffect(() => {
    if (!posFullscreen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPosFullscreen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [posFullscreen, setPosFullscreen])

  useEffect(() => () => setPosFullscreen(false), [setPosFullscreen])

  const fullscreenButton = posFullscreen ? (
    <Button
      variant="secondary"
      size="sm"
      onClick={exitFullscreen}
      aria-label="Exit POS fullscreen"
      title="Exit fullscreen"
    >
      <Minimize2 className="h-4 w-4" />
      Exit fullscreen
    </Button>
  ) : (
    <Button
      variant="secondary"
      size="sm"
      onClick={enterFullscreen}
      aria-label="Enter POS fullscreen"
      title="Enter fullscreen"
    >
      <Maximize2 className="h-4 w-4" />
      Fullscreen
    </Button>
  )

  // Keep all POS workspaces mounted. Besides preloading the whole route, this
  // preserves terminal state and prevents skeleton/empty flashes on tab changes.
  const renderTabContent = () => (
    <>
      <div className={posTab === 'tables' ? 'h-full' : 'hidden'} aria-hidden={posTab !== 'tables'}>
        {isStandalone ? (
          <POSTerminal
            tableName="Counter Sale"
            serviceMode="counter"
            onBack={() => undefined}
            showBack={false}
            headerActions={fullscreenButton}
          />
        ) : (
          <POSTablesView allowCounterSale={posMode === 'hybrid'} />
        )}
      </div>
      <div className={posTab === 'menu' ? 'h-full' : 'hidden'} aria-hidden={posTab !== 'menu'}>
        <POSMenuView />
      </div>
      <div className={posTab === 'reports' ? 'h-full' : 'hidden'} aria-hidden={posTab !== 'reports'}>
        <POSReportsView />
      </div>
    </>
  )

  // POS module disabled → render only the menu manager (no tables / reports / tabs).
  // The menu API endpoints don't require POS, so users on Free or without
  // featurePOS can still curate the menu they expose via the customer page.
  if (!featurePOS) {
    return (
      <div className="flex h-[calc(100dvh-8.75rem-env(safe-area-inset-bottom))] min-h-0 flex-col overflow-hidden md:h-[calc(100dvh-7rem)]">
        <PageHeader title="Menu" description="Manage categories and items" />
        <div className="min-h-0 flex-1 overflow-hidden">
          <POSMenuView />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={fullscreenRootRef}
      data-testid="pos-workspace"
      data-pos-fullscreen={posFullscreen ? 'true' : 'false'}
      className={cn(
        'flex min-h-0 flex-col overflow-hidden bg-[var(--color-bg-base)]',
        posFullscreen
          ? 'fixed inset-0 z-[100] h-dvh min-h-0 p-0'
          : 'h-[calc(100dvh-8.75rem-env(safe-area-inset-bottom))] md:h-[calc(100dvh-7rem)]',
      )}
    >
      {!posFullscreen && (
        <PageHeader
          title="POS System"
          description={isStandalone ? 'Counter sales, menu, and reports' : 'Table orders, menu, and reports'}
          actions={
            <>
              <span className="rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-1 text-xs font-semibold text-[var(--color-text-secondary)]">
                {POS_MODE_LABEL[posMode]}
              </span>
              {!isStandalone && fullscreenButton}
            </>
          }
        />
      )}

      {posFullscreen && !isStandalone && (
        <div className="flex min-h-12 shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {isStandalone ? 'Standalone POS' : 'POS System'}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">
              {isStandalone && posTab === 'tables' ? 'Counter terminal' : POS_MODE_LABEL[posMode]}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!isStandalone && (
              <span className="hidden rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-1 text-xs font-semibold text-[var(--color-text-secondary)] sm:inline-flex">
                {POS_MODE_LABEL[posMode]}
              </span>
            )}
            {fullscreenButton}
          </div>
        </div>
      )}

      {/* Tab bar */}
      {(!posFullscreen || !isStandalone || posTab !== 'tables') && (
      <div
        className={cn(
          'flex shrink-0 items-center gap-1 overflow-x-auto px-1',
          posFullscreen
            ? 'border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-2'
            : 'pb-4',
        )}
      >
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            type="button"
            key={key}
            onClick={() => setPosTab(key)}
            className={[
              'flex items-center gap-2 px-4 py-2 rounded-[var(--radius-md)] text-sm font-medium transition-colors',
              posTab === key
                ? 'bg-[var(--color-accent-amber)] text-black'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]',
            ].join(' ')}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>
      )}

      {/* Tab content */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {renderTabContent()}
      </div>
    </div>
  )
}
